import React, { useCallback, useEffect, useState } from 'react';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { Paragraph } from '@uncinc/react-drupal-core';
import WebformStepCounter from '@uncinc/react-drupal-core/dist/Components/Webform/WebformStepCounter';

import { ROUTE_TYPE_SELF_HELP } from '../../../config/constants';
import Modal from '../../../containers/Modal';
import useClasses from '../../../hooks/useClasses';
import useRoutes from '../../../hooks/useRoutes';
import useProgrammaticFocus from '../../../hooks/useProgrammaticFocus';
import { useModalContext } from '../../../containers/Modal/Context';
import type { ImmutableJoyrideNodeEntity } from '..';

interface NodeJoyrideProps {
  entity: ImmutableJoyrideNodeEntity;
}

const NodeJoyrideInner: React.FC<NodeJoyrideProps> = ({ entity }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { program } = useRoutes(ROUTE_TYPE_SELF_HELP);

  const { modalRef } = useModalContext();

  const [modalVisible, setModalVisible] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);

  const node = entity.toJS();
  const paragraphs = node.field_paragraphs;
  const totalSteps = paragraphs.length;

  const focusToBody = useProgrammaticFocus(document.body);

  const onCloseModal = useCallback(() => {
    setModalVisible(!modalVisible);
    history.push(program);
  }, [history, modalVisible, program]);

  const onPrev = useCallback(() => setCurrentStep(currentStep - 1), [currentStep]);
  const onNext = useCallback(() => setCurrentStep(currentStep + 1), [currentStep]);

  useEffect(() => {
    modalRef?.current?.scrollTo(0, 0);
    focusToBody();
  }, [currentStep, focusToBody, modalRef]);

  return (
    <>
      <WebformStepCounter
        pages={Array.from({ length: totalSteps }, (x, i) => i + 1)}
        currentPage={currentStep}
      />

      <div className="col-12 pb-18">
        {paragraphs.length > 0 && paragraphs.map((paragraph, index) => {
          const id = paragraph.target_id;
          const language = paragraph.entity.langcode[0].value;
          const step = index + 1;

          // Only return the current step.
          if (step !== currentStep) {
            return null;
          }

          return (
            <Paragraph
              key={`paragraph:${id}:${language}`}
              id={id}
              entity={Immutable.fromJS(paragraph.entity)}
            />
          );
        })}
      </div>

      <Modal.Actions
        primaryActionTitle={currentStep === totalSteps && t('buttons.get_started')}
        secondaryActionTitle={t('buttons.skip')}
        onBack={currentStep > 1 && onPrev}
        onPrimaryAction={currentStep === totalSteps ? onCloseModal : onNext}
        onSecondaryAction={currentStep === 1 && onCloseModal}
      />
    </>
  );
};

const NodeJoyride: React.FC<NodeJoyrideProps> = ({ entity }) => {
  const history = useHistory();
  const { buildClass, joinClasses } = useClasses('NodeJoyride');
  const { program } = useRoutes(ROUTE_TYPE_SELF_HELP);

  const [modalVisible, setModalVisible] = useState(true);

  const node = entity.toJS();
  const title = node.title[0].value;

  const onCloseModal = useCallback(() => {
    setModalVisible(!modalVisible);
    history.push(program);
  }, [history, modalVisible, program]);

  return (
    <Modal
      modalVisible={modalVisible}
      className={joinClasses(buildClass(), 'py-8')}
      title={title}
      closeModal={onCloseModal}
    >
      <NodeJoyrideInner entity={entity} />
    </Modal>
  );
};

export default NodeJoyride;
